import { chakra } from '@chakra-ui/react';
import * as React from 'react';

import {
  IFileSearch,
  IMemberSearch,
  IPostSearch,
  ViewableAttributesField,
} from '@/client/legacy-shared-modules/search/search-types';
import {
  CustomHighlight,
  getAlgoliaHighlightPropertyValue,
  parseAlgoliaHighlightHtml,
} from '@/components/CustomHighlight';
import { formatDisplayName } from '@/utilities';
import {
  collectMemberMatchedWords,
  findMergedMatchIntervals,
  splitTextByHighlightIntervals,
} from '@/utilities/memberSearchNameHighlight';

export interface FormattedUserNameProps {
  hit: IPostSearch | IMemberSearch | IFileSearch;
  viewableAttributesField: ViewableAttributesField;
  highlightAttributesField?: string;
   preferMemberFullNameMatchedWordHighlights?: boolean;
  fallbackDisplayName?: string;
}

function renderMemberFullNameWithHighlights(
  hit: IMemberSearch,
  viewableAttributesField: ViewableAttributesField,
  highlightAttributesField: string | undefined,
  fallbackDisplayName: string | undefined
): React.ReactNode {
  const authorAttributes = hit[viewableAttributesField];
  const fullName = authorAttributes
    ? formatDisplayName({
        preferredName: authorAttributes.preferredName,
        prefix: authorAttributes.prefix,
        firstName: authorAttributes.firstName,
        lastName: authorAttributes.lastName,
        suffix: authorAttributes.suffix,
        nameTitle: authorAttributes.nameTitle,
      })
    : fallbackDisplayName ?? hit.displayName ?? '';

  const searchablePrefix =
    highlightAttributesField ?? viewableAttributesField;
  const words = collectMemberMatchedWords(
    hit,
    searchablePrefix,
    viewableAttributesField
  );

  if (words.length > 0) {
    const intervals = findMergedMatchIntervals(fullName, words);
    if (intervals.length > 0) {
      const segments = splitTextByHighlightIntervals(fullName, intervals);
      return (
        <>
          {segments.map((seg, i) =>
            seg.isHighlighted ? (
              <chakra.span key={i} bg="blue.100">
                {seg.value}
              </chakra.span>
            ) : (
              <span key={i}>{seg.value}</span>
            )
          )}
        </>
      );
    }
  }

  const displayHl = getAlgoliaHighlightPropertyValue(
    hit,
    '_highlightResult',
    'displayName'
  );
  if (displayHl && /<(em|mark|ais-highlight)/i.test(displayHl)) {
    const parsed = parseAlgoliaHighlightHtml(displayHl);
    return (
      <>
        {parsed.map((p, i) =>
          p.isHighlighted ? (
            <chakra.span key={i} bg="blue.100">
              {p.value}
            </chakra.span>
          ) : (
            <span key={i}>{p.value}</span>
          )
        )}
      </>
    );
  }

  return <span>{fullName}</span>;
}

export const FormattedUserName: React.FC<FormattedUserNameProps> = ({
  hit,
  viewableAttributesField,
  highlightAttributesField,
  preferMemberFullNameMatchedWordHighlights,
  fallbackDisplayName,
}) => {
  if (preferMemberFullNameMatchedWordHighlights) {
    return (
      <>
        {renderMemberFullNameWithHighlights(
          hit as IMemberSearch,
          viewableAttributesField,
          highlightAttributesField,
          fallbackDisplayName
        )}
      </>
    );
  }

  const authorAttributes = hit[viewableAttributesField];
  const highlightPrefix =
    highlightAttributesField ?? viewableAttributesField;

  // Component to render highlighted name
  const renderHighlightedName = () => {
    // If we have preferredName, highlight it directly
    if (authorAttributes?.preferredName) {
      return (
        <CustomHighlight
          attribute={`${highlightPrefix}.preferredName`}
          hit={hit}
        />
      );
    }

    // For concatenated names, we need to highlight individual parts
    const nameParts = [];

    if (authorAttributes?.prefix && authorAttributes.prefix !== '') {
      nameParts.push(
        <CustomHighlight
          key="prefix"
          attribute={`${highlightPrefix}.prefix`}
          hit={hit}
        />
      );
      if (!authorAttributes.prefix.endsWith('.')) {
        nameParts.push('.');
      }
    }

    if (authorAttributes?.firstName) {
      nameParts.push(
        <CustomHighlight
          key="firstName"
          attribute={`${highlightPrefix}.firstName`}
          hit={hit}
        />
      );
    }

    if (authorAttributes?.lastName) {
      nameParts.push(' ');
      nameParts.push(
        <CustomHighlight
          key="lastName"
          attribute={`${highlightPrefix}.lastName`}
          hit={hit}
        />
      );
      nameParts.push(' ');
    }

    if (authorAttributes?.suffix && authorAttributes.suffix !== '') {
      nameParts.push(
        <CustomHighlight
          key="suffix"
          attribute={`${highlightPrefix}.suffix`}
          hit={hit}
        />
      );
      if (!authorAttributes.suffix.endsWith('.')) {
        nameParts.push('.');
      }
    }

    if (authorAttributes?.nameTitle && authorAttributes.nameTitle !== '') {
      nameParts.push(', ');
      nameParts.push(
        <CustomHighlight
          key="nameTitle"
          attribute={`${highlightPrefix}.nameTitle`}
          hit={hit}
        />
      );
    }

    // If no individual parts are available, fall back to displayName
    if (nameParts.length === 0) {
      return <CustomHighlight attribute="displayName" hit={hit} />;
    }
    return <>{nameParts}</>;
  };

  return <>{renderHighlightedName()}</>;
};
